import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Tag,
  Space,
  message,
  Alert,
  Spin,
  Typography,
  Divider,
  List,
} from "antd";
import {
  ShoppingCartOutlined,
  LinkOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { PicqerService } from "../../services/picqerService";
import { WmsPurchaseOrderService } from "../../services/wmsPurchaseOrderService";
import { supabase } from "../../lib/supabase";

const { Text, Title } = Typography;

interface PurchaseOrderManagementProps {
  bookingId: number;
  bookingReference: string;
  companyId: number;
  onRefresh?: () => void;
}

interface PurchaseOrder {
  id: number;
  picqer_reference: string | null;
  booking_reference: string | null;
  picqer_purchase_order_id: number | null;
  picqer_purchaseorderid?: string | null;
  status: string;
  supplier_name?: string | null;
  created_at: string;
  channeldock_delivery_id?: number | null;
  channeldock_reference?: string | null;
}

interface ProductAnalysis {
  id: number;
  name: string;
  ean: string | null;
  pieces: number | null;
  cartons: number | null;
  picqerProductId: number | null;
  companyProductId: number | null;
  status: "ready" | "needs_picqer_link" | "needs_company_product" | "needs_ean";
  canInclude: boolean;
}

interface ProductAnalysisResponse {
  total: number;
  ready: number;
  needingSetup: number;
  products: ProductAnalysis[];
}

export const PurchaseOrderManagement: React.FC<
  PurchaseOrderManagementProps
> = ({ bookingId, bookingReference, companyId, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [productAnalysis, setProductAnalysis] =
    useState<ProductAnalysisResponse | null>(null);
  const [picqerIntegrationValid, setPicqerIntegrationValid] = useState(false);
  const [integrationError, setIntegrationError] = useState<string | null>(null);
  const [wmsProvider, setWmsProvider] = useState<"picqer" | "channeldock">("picqer");

  useEffect(() => {
    loadData();
  }, [bookingId]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        checkPicqerIntegration(),
        loadPurchaseOrders(),
        loadProductAnalysis(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const checkPicqerIntegration = async () => {
    try {
      const { data: companyWms, error } = await supabase
        .from("company")
        .select("wms_provider, picqer_enabled, picqer_api_key, picqer_fulfilment_customer_id, channeldock_enabled, channeldock_api_key, channeldock_api_secret")
        .eq("id", companyId)
        .single();

      if (error || !companyWms) {
        setPicqerIntegrationValid(false);
        setIntegrationError("Unable to check WMS integration");
        return;
      }

      if (companyWms.wms_provider === "channeldock") {
        const valid =
          !!companyWms.channeldock_enabled &&
          !!companyWms.channeldock_api_key &&
          !!companyWms.channeldock_api_secret;
        setWmsProvider("channeldock");
        setPicqerIntegrationValid(valid);
        setIntegrationError(valid ? null : "Company not connected to ChannelDock");
        return;
      }

      setWmsProvider("picqer");
      if (!companyWms.picqer_enabled || !companyWms.picqer_api_key) {
        setPicqerIntegrationValid(false);
        setIntegrationError("Company not connected to Picqer");
        return;
      }
      if (!companyWms.picqer_fulfilment_customer_id) {
        setPicqerIntegrationValid(false);
        setIntegrationError("No fulfillment customer configured");
        return;
      }
      setPicqerIntegrationValid(true);
      setIntegrationError(null);
    } catch (err) {
      console.error("Error checking WMS integration:", err);
      setPicqerIntegrationValid(false);
      setIntegrationError("Error checking integration");
    }
  };

  const loadPurchaseOrders = async () => {
    try {
      const result = await PicqerService.getPurchaseOrdersForBooking(bookingId);
      if (result.success && result.data) {
        setPurchaseOrders(result.data);
      } else {
        setPurchaseOrders([]);
      }
    } catch (error) {
      console.error("Error loading purchase orders:", error);
      setPurchaseOrders([]);
    }
  };

  const loadProductAnalysis = async () => {
    try {
      const { data: companyWms } = await supabase
        .from("company")
        .select("wms_provider")
        .eq("id", companyId)
        .single();
      const provider = companyWms?.wms_provider === "channeldock" ? "channeldock" : "picqer";
      const result = await WmsPurchaseOrderService.analyzeProductsForBooking(bookingId, provider);
      if (result.success && result.data) {
        setProductAnalysis(result.data as ProductAnalysisResponse);
      }
    } catch (error) {
      console.error("Error loading product analysis:", error);
    }
  };

  const handleCreatePurchaseOrder = async () => {
    if (!picqerIntegrationValid) {
      message.error(integrationError || "WMS integration not configured");
      return;
    }

    setCreating(true);
    try {
      const { data: companyWms } = await supabase
        .from("company")
        .select("wms_provider")
        .eq("id", companyId)
        .single();
      const provider = companyWms?.wms_provider === "channeldock" ? "channeldock" : "picqer";
      const result = await WmsPurchaseOrderService.createPurchaseOrderForBooking(bookingId, provider);

      if (result.success && result.data) {
        message.success("Purchase order created successfully! ✓");

        // Mark booking as completed now that PO is created
        const { error: updateError } = await supabase
          .from("bookings")
          .update({ unloading_status: "completed" })
          .eq("id", bookingId);

        if (updateError) {
          console.error("Error marking booking as completed:", updateError);
        }

        await loadData();
        onRefresh?.();
      } else {
        message.error(`Failed to create purchase order: ${result.error}`);
      }
    } catch (error) {
      console.error("Error creating purchase order:", error);
      message.error("An error occurred while creating the purchase order");
    } finally {
      setCreating(false);
    }
  };

  const openInWms = (po: PurchaseOrder) => {
    if (po.channeldock_delivery_id != null) {
      window.open("https://channeldock.com", "_blank");
    } else if (po.picqer_purchase_order_id != null) {
      window.open(`https://e-freight-forwarding.picqer.com/purchaseorders/${po.picqer_purchase_order_id}`, "_blank");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "concept":
        return "blue";
      case "expected":
        return "orange";
      case "purchased":
        return "green";
      case "cancelled":
        return "red";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case "concept":
        return "Draft";
      case "expected":
        return "Expected";
      case "purchased":
        return "Purchased";
      case "cancelled":
        return "Cancelled";
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading purchase order information...</Text>
        </div>
      </div>
    );
  }

  // If there's a purchase order, show it prominently
  if (purchaseOrders.length > 0) {
    const po = purchaseOrders[0];
    return (
      <div>
        <Card
          style={{
            borderRadius: 12,
            backgroundColor: "#f0f9ff",
            border: "2px solid #1890ff",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <ShoppingCartOutlined
              style={{ fontSize: 48, color: "#1890ff", marginBottom: 12 }}
            />
            <Title level={4} style={{ marginBottom: 8 }}>
              Purchase Order Created
            </Title>
            <Tag
              color={getStatusColor(po.status)}
              style={{ fontSize: 14, padding: "4px 12px", marginBottom: 16 }}
            >
              {getStatusLabel(po.status)}
            </Tag>

            <Divider style={{ margin: "16px 0" }} />

            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Reference
              </Text>
              <div>
                <Text strong style={{ fontSize: 16 }}>
                  {po.booking_reference || po.picqer_reference}
                </Text>
              </div>
            </div>

            {po.supplier_name && (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Supplier
                </Text>
                <div>
                  <Text style={{ fontSize: 14 }}>{po.supplier_name}</Text>
                </div>
              </div>
            )}

            <Button
              type="primary"
              size="large"
              icon={<LinkOutlined />}
              onClick={() => openInWms(po)}
              block
              style={{
                height: 48,
                fontSize: 16,
                fontWeight: 600,
                marginTop: 8,
              }}
            >
              Open in WMS
            </Button>

            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={loadData}
              block
              style={{ marginTop: 8 }}
            >
              Refresh
            </Button>
          </div>
        </Card>

        {/* Product status info */}
        {productAnalysis && (
          <Card
            style={{
              marginTop: 12,
              borderRadius: 8,
            }}
          >
            <Space direction="vertical" style={{ width: "100%" }} size="small">
              <Text strong>Products Status</Text>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: 6,
                }}
              >
                <Text type="secondary">Ready for PO:</Text>
                <Text strong>
                  {productAnalysis.ready} / {productAnalysis.total}
                </Text>
              </div>
              {productAnalysis.needingSetup > 0 && (
                <Alert
                  message={`${productAnalysis.needingSetup} product(s) need setup in WMS before they can be included`}
                  type="warning"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>
        )}
      </div>
    );
  }

  // No purchase order - show creation interface
  return (
    <div>
      {!picqerIntegrationValid ? (
        <Alert
          message="WMS Integration Required"
          description={
            integrationError ||
            "Company needs to be connected to a WMS (Picqer or ChannelDock) to create purchase orders"
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Card
          style={{
            borderRadius: 12,
            textAlign: "center",
          }}
        >
          <ShoppingCartOutlined
            style={{ fontSize: 64, color: "#d9d9d9", marginBottom: 16 }}
          />
          <Title level={4} style={{ marginBottom: 8 }}>
            No Purchase Order Yet
          </Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
            Create a purchase order in your WMS to manage inventory for this
            storage booking
          </Text>

          {productAnalysis && (
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text strong>Product Status:</Text>
                    <Text>
                      {productAnalysis.ready} / {productAnalysis.total} ready
                    </Text>
                  </div>
                  {productAnalysis.needingSetup > 0 && (
                  <Alert
                    message={`${productAnalysis.needingSetup} product(s) need setup`}
                    description="Products without EAN codes or not linked to WMS cannot be included in the purchase order"
                    type="warning"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                  )}
                </Space>
              </div>

              {productAnalysis.products &&
                productAnalysis.products.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <List
                      size="small"
                      dataSource={productAnalysis.products}
                      renderItem={(product) => (
                        <List.Item
                          style={{
                            padding: "8px 12px",
                            backgroundColor: product.canInclude
                              ? "#f6ffed"
                              : "#fff7e6",
                            borderRadius: 6,
                            marginBottom: 4,
                          }}
                        >
                          <Space
                            style={{
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <div style={{ textAlign: "left" }}>
                              <Text
                                strong
                                style={{
                                  fontSize: 13,
                                  display: "block",
                                  marginBottom: 2,
                                }}
                              >
                                {product.name}
                              </Text>
                              {product.ean && (
                                <Text
                                  type="secondary"
                                  style={{ fontSize: 11, display: "block" }}
                                >
                                  EAN: {product.ean}
                                </Text>
                              )}
                              {product.picqerProductId && (
                                <Text
                                  type="secondary"
                                  style={{ fontSize: 11, display: "block" }}
                                >
                                  {wmsProvider === "channeldock"
                                    ? `WMS ID: ${product.picqerProductId}`
                                    : (
                                        <>
                                          WMS ID:{" "}
                                          <a
                                            href={`https://e-freight-forwarding.picqer.com/products/${product.picqerProductId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "#1890ff" }}
                                          >
                                            {product.picqerProductId}
                                          </a>
                                        </>
                                      )}
                                </Text>
                              )}
                            </div>
                            {product.canInclude ? (
                              <CheckCircleOutlined
                                style={{ color: "#52c41a", fontSize: 18 }}
                              />
                            ) : (
                              <CloseCircleOutlined
                                style={{ color: "#faad14", fontSize: 18 }}
                              />
                            )}
                          </Space>
                        </List.Item>
                      )}
                    />
                  </div>
                )}
            </div>
          )}

          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={handleCreatePurchaseOrder}
            loading={creating}
            disabled={
              creating ||
              !picqerIntegrationValid ||
              (productAnalysis && productAnalysis.ready === 0)
            }
            block
            style={{
              height: 48,
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            {creating
              ? "Creating..."
              : productAnalysis && productAnalysis.ready === 0
              ? "No Products Ready"
              : "Create Purchase Order"}
          </Button>

          <Button
            type="text"
            icon={<ReloadOutlined />}
            onClick={loadData}
            block
            style={{ marginTop: 8 }}
          >
            Refresh
          </Button>
        </Card>
      )}
    </div>
  );
};
